/**
 * @module ReceiptServiceApi
 * @remarks RTK Query service für Belegverwaltung und Bildupload
 */

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { ReceiptData, ReceiptResponse, UploadImageResponse, UploadImageRequest, UploadPicResponse } from '@models/upload-service.model';

export const receiptServiceApi = createApi({
  reducerPath: 'receiptServiceApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/',
    prepareHeaders: (headers) => {
      const token = localStorage.getItem("auth_token");
      if (token) {
        headers.set('Authorization', token);
      }
      return headers;
    }
  }),
  tagTypes: ['Receipt'],
  endpoints: (builder) => ({
    // Belegdaten erstellen
    createReceipt: builder.mutation<ReceiptResponse[], ReceiptData>({
      query: (receiptData) => ({
        url: 'data/nachbuchungen',
        method: 'PUT',
        body: receiptData
      }),
      // Optimistische Aktualisierung der Cache-Tags
      invalidatesTags: ['Receipt']
    }),
    
    // Belegbild hochladen
    uploadReceiptImage: builder.mutation<UploadImageResponse, UploadImageRequest>({
      query: ({ formData, recordId }) => {
        const url = recordId ? `/upload?recordId=${recordId}` : '/upload';
        return {
          url,
          method: 'POST',
          body: formData,
        };
      }
    }),
    
    // Belegbild mit dem Record verknüpfen - Verwende lokale API-Route
    associateReceiptImage: builder.mutation<UploadPicResponse, { recordId: number, uploadId: string }>({
      query: ({ recordId, uploadId }) => {        
        return {
          url: 'exec', // Verwende lokale Route statt direkten Ninox-Aufruf
          method: 'POST',
          body: {
            query: `uploadPic(${recordId},"${uploadId}")`
          }
        };
      }
    }),
    
    // Kombinierte Funktion für den gesamten Workflow
    createReceiptWithImage: builder.mutation<
      { receipt: ReceiptResponse, upload: UploadImageResponse, association: UploadPicResponse }, 
      { receiptData: ReceiptData, imageFile: File }
    >({
      async queryFn(
        { receiptData, imageFile }, 
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        { dispatch }, 
        _extraOptions, 
        baseQuery
      ) {
        try {
          // Schritt 1: Belegdaten erstellen
          const createReceiptResult = await baseQuery({
            url: 'data/nachbuchungen',
            method: 'PUT',
            body: receiptData
          });
          
          if (createReceiptResult.error) {
            return { error: createReceiptResult.error };
          }
          
          const receiptResponse = createReceiptResult.data as ReceiptResponse[];
          if (!receiptResponse || receiptResponse.length === 0) {
            return { 
              error: { 
                status: 'CUSTOM_ERROR', 
                error: 'Keine Antwort von der Beleg-API erhalten' 
              } 
            };
          }
          
          const createdReceiptId = receiptResponse[0].id;
          
          // Schritt 2: Beleg-Bild hochladen
          const formData = new FormData();
          formData.append('file', imageFile);
          
          const uploadResult = await baseQuery({
            url: `/upload?recordId=FB${createdReceiptId}`,
            method: 'POST',
            body: formData
          });
          
          if (uploadResult.error) {
            return { error: uploadResult.error };
          }
          
          const uploadResponse = uploadResult.data as UploadImageResponse;
          
          // Wichtig: Wir verwenden die uploadId aus der uploadResponse,
          // nicht den ursprünglichen Dateinamen
          if (!uploadResponse.uploadId) {
            return { 
              error: { 
                status: 'CUSTOM_ERROR', 
                error: 'Keine Upload-ID vom Server erhalten' 
              } 
            };
          }
          
          // Schritt 3: Bild mit dem Record verknüpfen - Verwende lokale API-Route und die uploadId
          const associateResult = await baseQuery({
            url: 'exec',
            method: 'POST',
            body: {
              query: `uploadPic(${createdReceiptId},"${uploadResponse.uploadId}")`
            }
          });
          
          if (associateResult.error) {
            return { error: associateResult.error };
          }
          
          const associateResponse = associateResult.data as UploadPicResponse;
          
          // Erfolgreiche Antwort mit allen Daten
          return { 
            data: {
              receipt: receiptResponse[0],
              upload: uploadResponse,
              association: associateResponse
            }
          };
        } catch (error) {
          return { 
            error: { 
              status: 'CUSTOM_ERROR', 
              error: `Ein unerwarteter Fehler ist aufgetreten: ${error}` 
            } 
          };
        }
      },
      // Optimistische Aktualisierung der Cache-Tags
      invalidatesTags: ['Receipt']
    })
  })
});

// Export der generierten Hooks für die Verwendung in Komponenten
export const {
  useCreateReceiptMutation,
  useUploadReceiptImageMutation,
  useAssociateReceiptImageMutation,
  useCreateReceiptWithImageMutation
} = receiptServiceApi;